/**
 * Header-only ISOBMFF top-level box walk for the sealed-upload chunked-object transport
 * (spec 2026-09-04 §Transport step 1). The client needs three facts about a selected MP4 —
 * where `ftyp`, `moov` and the first `mdat` sit — so it can (a) decide whether to OFFER the
 * chunked-object transport at all and (b) upload the parts covering those spans first, which is
 * what lets the server's speculative pre-encode start before the upload ends.
 *
 * It reads only box HEADERS (8 or 16 bytes each) and skips bodies by their declared size, so a
 * moov-last 2 GiB phone recording costs about four small reads, not a scan. Sample tables are not
 * parsed here; the server's `locateHeaderLayout` / `parseIsobmffSampleTables` own that.
 *
 * Pure: no I/O of its own (the reader is injected), no globals, never throws — every failure is a
 * typed decline, and a decline only ever means "upload as multipart, like today".
 */

export interface IsobmffByteSpan {
	/** Inclusive start offset. */
	start: number;
	/** Exclusive end offset. */
	end: number;
}

export interface IsobmffLayout {
	kind: "faststart" | "moov-last";
	fileSize: number;
	ftyp: IsobmffByteSpan;
	moov: IsobmffByteSpan;
	/** The first mdat; `headerEnd` is the end of its 8/16-byte header, the only mdat bytes the server needs early. */
	mdat: IsobmffByteSpan & { headerEnd: number };
	topLevelBoxCount: number;
	reads: number;
}

export type IsobmffLayoutDeclineReason =
	| "too-small"
	| "no-ftyp"
	| "fragmented"
	| "moov-missing"
	| "mdat-missing"
	| "box-size-invalid"
	| "box-truncated"
	| "too-many-boxes"
	| "moov-too-large"
	| "timeout"
	| "read-error";

export type IsobmffLayoutResult = { ok: true; layout: IsobmffLayout } | { ok: false; reason: IsobmffLayoutDeclineReason; detail?: string };

export interface IsobmffByteReader {
	size: number;
	read(start: number, end: number): Promise<Uint8Array>;
}

/**
 * Budget for the walk itself: it is checked BETWEEN reads (before and after each `read` resolves),
 * so it bounds the number of further reads a slow file gets, not any single one. A `read` that hangs
 * forever is NOT interruptible from here — there is no abort signal on `IsobmffByteReader`, so an
 * unresolved promise simply never returns. Callers that must not stall (a cloud placeholder that
 * never hydrates) have to race the whole `locateIsobmffLayout` call against their own deadline and
 * treat the loss as a decline; the facade client does exactly that.
 */
export const ISOBMFF_LAYOUT_TIMEOUT_MS = 5_000;
/** More top-level boxes than this is not a media file this lane wants; the server's walker has the same shape of bound. */
export const ISOBMFF_LAYOUT_MAX_BOXES = 64;
/**
 * A `moov` larger than this is not a file this lane offers the transport for.
 *
 * The span is the box's OWN declared size, and it is the one range that later gets read whole — here to
 * decide the layout, and on the server (`sealedMovieReader.MAX_MOOV_BYTES`, the same 256 MiB) to parse
 * the sample tables out of a single `Buffer.alloc`. Declining on this side keeps a file whose header the
 * server will refuse from being offered as `chunked-object` at all. 256 MiB is far past any real moov:
 * sample tables cost roughly 16-20 bytes per sample.
 */
export const ISOBMFF_LAYOUT_MAX_MOOV_BYTES = 256 * 1024 * 1024;

const BOX_HEADER_BYTES = 8;
const LARGESIZE_HEADER_BYTES = 16;

function readUint32(bytes: Uint8Array, offset: number): number {
	return ((bytes[offset] << 24) | (bytes[offset + 1] << 16) | (bytes[offset + 2] << 8) | bytes[offset + 3]) >>> 0;
}

function readUint64AsNumber(bytes: Uint8Array, offset: number): number | null {
	let value = 0n;
	for (let index = 0; index < 8; index++) value = (value << 8n) | BigInt(bytes[offset + index]);
	return value <= BigInt(Number.MAX_SAFE_INTEGER) ? Number(value) : null;
}

function readBoxType(bytes: Uint8Array, offset: number): string {
	return String.fromCharCode(bytes[offset], bytes[offset + 1], bytes[offset + 2], bytes[offset + 3]);
}

/**
 * Reads a Blob slice as bytes. `Blob.arrayBuffer` is missing on older Safari (and on jsdom's Blob),
 * so fall back to `FileReader` exactly as `liveSnapshotUploadLane.sha256Hex` does — a header read
 * that throws here would decline an otherwise perfectly eligible file.
 */
async function readBlobBytes(slice: Blob): Promise<Uint8Array> {
	if (typeof slice.arrayBuffer === "function") return new Uint8Array(await slice.arrayBuffer());
	const buffer = await new Promise<ArrayBuffer>((resolve, reject) => {
		const fileReader = new FileReader();
		fileReader.onload = () =>
			fileReader.result instanceof ArrayBuffer ? resolve(fileReader.result) : reject(new Error("ISOBMFF header read failed"));
		fileReader.onerror = () => reject(fileReader.error ?? new Error("ISOBMFF header read failed"));
		fileReader.readAsArrayBuffer(slice);
	});
	return new Uint8Array(buffer);
}

export function blobByteReader(blob: Blob): IsobmffByteReader {
	return {
		size: blob.size,
		read: (start, end) => readBlobBytes(blob.slice(start, end))
	};
}

export async function locateIsobmffLayout(
	reader: IsobmffByteReader,
	options: { timeoutMs?: number; maxBoxes?: number; now?: () => number } = {}
): Promise<IsobmffLayoutResult> {
	const timeoutMs = options.timeoutMs ?? ISOBMFF_LAYOUT_TIMEOUT_MS;
	const maxBoxes = options.maxBoxes ?? ISOBMFF_LAYOUT_MAX_BOXES;
	const now = options.now ?? (() => Date.now());
	const fileSize = reader.size;
	if (!Number.isSafeInteger(fileSize) || fileSize < BOX_HEADER_BYTES) return { ok: false, reason: "too-small" };

	const startedAt = now();
	let reads = 0;
	let offset = 0;
	let boxCount = 0;
	let ftyp: IsobmffByteSpan | null = null;
	let moov: IsobmffByteSpan | null = null;
	let mdat: (IsobmffByteSpan & { headerEnd: number }) | null = null;

	// A trailing run shorter than one box header cannot be a box, so the walk stops and ignores it.
	// That means `mdat.end` (and the last box's end) may be less than `fileSize` on a file with a
	// stray tail — deliberate: the layout describes the boxes it proved, not the whole file.
	while (offset + BOX_HEADER_BYTES <= fileSize) {
		if (now() - startedAt > timeoutMs) return { ok: false, reason: "timeout", detail: `after ${reads} reads` };
		if (boxCount >= maxBoxes) return { ok: false, reason: "too-many-boxes" };
		let header: Uint8Array;
		try {
			header = await reader.read(offset, Math.min(offset + LARGESIZE_HEADER_BYTES, fileSize));
			reads++;
		} catch (error) {
			return {
				ok: false,
				reason: "read-error",
				detail: error instanceof Error ? error.message : String(error)
			};
		}
		if (now() - startedAt > timeoutMs) return { ok: false, reason: "timeout", detail: `after ${reads} reads` };
		// The reader is third-party (a Blob, a File, a caller's own stub). A non-view result — `undefined`
		// from a stub, a plain ArrayBuffer, anything — would make the byte accessors below yield `NaN`
		// and mint a garbage layout, so it declines like any other read failure. Never throws.
		if (!ArrayBuffer.isView(header))
			return {
				ok: false,
				reason: "read-error",
				detail: `reader returned a non-view (${typeof header}) at ${offset}`
			};
		if (header.length < BOX_HEADER_BYTES)
			return {
				ok: false,
				reason: "box-truncated",
				detail: `header at ${offset}`
			};
		const size32 = readUint32(header, 0);
		const type = readBoxType(header, 4);
		if (boxCount === 0 && type !== "ftyp")
			return {
				ok: false,
				reason: "no-ftyp",
				detail: `first box ${JSON.stringify(type)}`
			};

		let headerEnd = offset + BOX_HEADER_BYTES;
		let boxEnd: number;
		if (size32 === 1) {
			if (header.length < LARGESIZE_HEADER_BYTES)
				return {
					ok: false,
					reason: "box-truncated",
					detail: `largesize header at ${offset}`
				};
			const size64 = readUint64AsNumber(header, BOX_HEADER_BYTES);
			if (size64 === null || size64 < LARGESIZE_HEADER_BYTES)
				return {
					ok: false,
					reason: "box-size-invalid",
					detail: `${type} at ${offset}`
				};
			headerEnd = offset + LARGESIZE_HEADER_BYTES;
			boxEnd = offset + size64;
		} else if (size32 === 0) {
			boxEnd = fileSize;
		} else {
			if (size32 < BOX_HEADER_BYTES)
				return {
					ok: false,
					reason: "box-size-invalid",
					detail: `${type} at ${offset} size ${size32}`
				};
			boxEnd = offset + size32;
		}
		if (boxEnd > fileSize)
			return {
				ok: false,
				reason: "box-truncated",
				detail: `${type} at ${offset} ends at ${boxEnd} > ${fileSize}`
			};

		if (type === "moof") return { ok: false, reason: "fragmented" };
		if (type === "ftyp" && ftyp === null) ftyp = { start: offset, end: boxEnd };
		if (type === "moov" && moov === null) {
			// BEFORE it is recorded as the span anything will read: this is the one large range the layout
			// hands on, so the decline has to happen while it is still a number rather than a read.
			if (boxEnd - offset > ISOBMFF_LAYOUT_MAX_MOOV_BYTES)
				return {
					ok: false,
					reason: "moov-too-large",
					detail: `${boxEnd - offset} bytes at ${offset}`
				};
			moov = { start: offset, end: boxEnd };
		}
		if (type === "mdat" && mdat === null) mdat = { start: offset, end: boxEnd, headerEnd };

		boxCount++;
		offset = boxEnd;
	}

	// Unreachable in practice and kept as a safety net: the `boxCount === 0` gate above rejects any
	// file whose first box is not `ftyp`, and a file too small for one header already returned
	// `too-small`, so reaching the end of the walk with `ftyp === null` is impossible today. Left in
	// so a future change to that gate degrades to a decline instead of an `ftyp` of `null!`.
	if (ftyp === null) return { ok: false, reason: "no-ftyp" };
	if (moov === null) return { ok: false, reason: "moov-missing" };
	if (mdat === null) return { ok: false, reason: "mdat-missing" };
	return {
		ok: true,
		layout: {
			kind: moov.start < mdat.start ? "faststart" : "moov-last",
			fileSize,
			ftyp,
			moov,
			mdat,
			topLevelBoxCount: boxCount,
			reads
		}
	};
}
